import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { HashLink } from "react-router-hash-link";
import { motion, AnimatePresence } from "framer-motion";
import { FiSun, FiMoon, FiMenu, FiX } from "react-icons/fi";
import logo from "../../assets/logo_500x500px_ei_harsoa.webp";

const navItems = [
  { label: "Meistä", to: "meista" },
  { label: "Ohjelmisto", to: "ohjelmisto" },
  { label: "Media", to: "media" },
  { label: "UKK", to: "useinkysyttya" },
  { label: "Varaa", to: "varaa" },
];

// Navbarin korkeus scroll-offsetiin
const OFFSET = 70;
const scrollWithOffset = (el) => {
  const y = el.getBoundingClientRect().top + window.pageYOffset - OFFSET;
  window.scrollTo({ top: y, behavior: "smooth" });
};

export default function Navbar() {
  const [theme, setTheme] = useState("dark");
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [scrollProgress, setScrollProgress] = useState(0);
  const isDark = theme === "dark";

  const ga = (name, data = {}) => {
    if (window.gaEvent) window.gaEvent(name, data);
  };

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
  }, [theme]);

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 10);
      const totalHeight = document.body.scrollHeight - window.innerHeight;
      setScrollProgress((window.scrollY / totalHeight) * 100);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  const toggleMenu = () => {
    const next = !open;
    setOpen(next);
    ga(next ? "mobiilivalikko_avattu" : "mobiilivalikko_suljettu");
  };

  const toggleTheme = () => {
    const next = theme === "light" ? "dark" : "light";
    setTheme(next);
    ga("teema_vaihdettu", { teema: next });
  };

  return (
    <>
      {/* Navbar */}
      <motion.nav
        initial={{ y: -60, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.6, ease: "easeOut" }}
        className={`fixed top-0 left-0 w-full z-50 bg-white dark:bg-joyrideBars transition-shadow duration-300 ${
          scrolled ? "shadow-md" : ""
        }`}
        role="navigation"
        aria-label="Päävalikko"
      >
        <div className="flex items-center justify-between h-16 px-4 max-w-7xl mx-auto">
          {/* Logo */}
          <Link
            to="/"
            aria-label="Siirry etusivulle"
            onClick={() => ga("logo_klikattu")}
          >
            <img
              src={logo}
              alt="Bilebändi Joyride logo"
              className="w-28 h-auto hover:scale-105 transition-transform"
              width="112"
              height="112"
            />
          </Link>

          {/* Desktop-nav */}
          <div className="hidden md:flex space-x-8">
            {navItems.map(({ label, to }) => (
              <HashLink
                key={to}
                to={`/#${to}`}
                scroll={scrollWithOffset}
                onClick={() => ga("nav_klikattu", { osio: label })}
                className="relative group text-joyrideNavy dark:text-joyrideLight font-medium"
              >
                {label}
                <span className="absolute left-0 -bottom-1 w-full h-0.5 bg-joyrideCyan scale-x-0 group-hover:scale-x-100 transition-transform origin-left" />
              </HashLink>
            ))}
          </div>

          {/* Right side */}
          <div className="flex items-center space-x-3">
        <button
  type="button"
  onClick={toggleTheme}
  aria-label={isDark ? "Vaihda vaaleaan teemaan" : "Vaihda tummaan teemaan"}
  aria-pressed={isDark}
className={`relative h-9 w-16 rounded-full border shadow-inner transition hover:scale-105 ${
  isDark
    ? "border-joyrideLight/60 bg-joyrideLight"
    : "border-joyrideNavy/30 bg-joyrideNavy"
}`}
>
  <motion.span
    initial={false}
    animate={{
      x: isDark ? 30 : 2
    }}
    transition={{
      type: "spring",
      stiffness: 420,
      damping: 28
    }}
className={`absolute left-0.5 top-0.5 grid h-8 w-8 place-items-center rounded-full shadow-md ${
  isDark
    ? "bg-joyrideNavy text-joyrideLight"
    : "bg-joyrideLight text-joyrideNavy"
}`}
  >
    <AnimatePresence mode="wait" initial={false}>
      {isDark ? (
        <motion.span
          key="sun"
          initial={{ rotate: -90, opacity: 0, scale: 0.6 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: 90, opacity: 0, scale: 0.6 }}
          transition={{ duration: 0.2 }}
        >
          <FiSun size={18} />
        </motion.span>
      ) : (
        <motion.span
          key="moon"
          initial={{ rotate: 90, opacity: 0, scale: 0.6 }}
          animate={{ rotate: 0, opacity: 1, scale: 1 }}
          exit={{ rotate: -90, opacity: 0, scale: 0.6 }}
          transition={{ duration: 0.2 }}
        >
          <FiMoon size={18} />
        </motion.span>
      )}
    </AnimatePresence>
  </motion.span>
</button>

<button
  onClick={toggleMenu}
  className="md:hidden p-2 rounded-full text-joyrideNavy transition hover:bg-gray-200 dark:text-joyrideLight dark:hover:bg-gray-700"
  aria-label={open ? "Sulje valikko" : "Avaa valikko"}
>
  {open ? <FiX size={24} /> : <FiMenu size={24} />}
</button>
          </div>
        </div>
      </motion.nav>

      {/* Scroll-progress */}
      <div
        className="fixed top-0 left-0 h-1 bg-joyrideCyan z-[60]"
        style={{ width: `${scrollProgress}%` }}
        aria-hidden="true"
      />

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="md:hidden fixed top-16 left-0 w-full bg-white dark:bg-joyrideBars shadow-lg z-40"
          >
            <div className="flex flex-col items-center space-y-4 py-6">
              {navItems.map(({ label, to }) => (
                <HashLink
                  key={to}
                  to={`/#${to}`}
                  scroll={scrollWithOffset}
                  onClick={() => {
                    ga("nav_klikattu", { osio: label, laite: "mobiili" });
                    setOpen(false);
                  }}
                  className="text-xl text-joyrideNavy dark:text-joyrideLight hover:text-joyrideCyan transition"
                >
                  {label}
                </HashLink>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
